import {
  bytesHash,
  statementSelectionSchema,
  parseContext,
  type Project,
  type ProofRecord,
  type Values,
} from "@proof/core";
import { bindPrepopulation } from "./prepopulation";
import type { LegacyEnvelope, LegacyRow } from "./index";
export class CustomizationError extends Error {}
export interface CustomizationIds {
  mode: "portable" | "database";
  stateId: number;
  variableIds?: Record<string, number>;
  partIds?: Record<string, number>;
  mainPartId?: number;
  complete?: boolean;
}
interface IdMap {
  mode: "portable" | "database";
  stateId: number;
  variableIds: Record<string, number>;
  partIds: Record<string, number>;
  mainPartId: number;
}
interface Sidecar {
  version: 1;
  projectId: string;
  tenantId: string;
  ids: IdMap;
  proof: ProofRecord;
  digest: string;
}
const states = "ESM_CUSTOMIZATION_STATE",
  variables = "ESM_CUSTOMIZATION_STATE_VARIABLE";
const own = (object: unknown, key: string): unknown =>
  object && typeof object === "object" && Object.hasOwn(object, key)
    ? (object as Record<string, unknown>)[key]
    : undefined;
const integer = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value > 0 &&
  value <= 2147483647;
const numeric = (value: unknown) =>
  typeof value === "number"
    ? value
    : typeof value === "string" && /^\d+$/.test(value)
      ? Number(value)
      : NaN;
const tokenId = (id: string, prefix: string) =>
  id.startsWith(`${prefix}_`) ? numeric(id.slice(prefix.length + 1)) : NaN;
const flag = (value: unknown) =>
  value === true || value === 1 || value === "1" || value === "Y";
function rows(envelope: LegacyEnvelope, table: string): LegacyRow[] {
  if (
    !envelope ||
    !envelope.tables ||
    typeof envelope.tables !== "object" ||
    Array.isArray(envelope.tables)
  )
    throw new CustomizationError("CUSTOMIZATION_TABLE_INVALID");
  const result = envelope.tables[table] || [];
  if (
    !Array.isArray(result) ||
    result.length > 100000 ||
    result.some((row) => !row || typeof row !== "object" || Array.isArray(row))
  )
    throw new CustomizationError("CUSTOMIZATION_TABLE_INVALID");
  const primary = table === states ? "CUSTOMIZATION_STATE_ID" : "ROW_ID",
    seen = new Set<number>();
  for (const row of result)
    if (Object.hasOwn(row, primary)) {
      const id = numeric(row[primary]);
      if (!integer(id) || seen.has(id))
        throw new CustomizationError("CUSTOMIZATION_ROW_ID_INVALID");
      seen.add(id);
    }
  return result;
}
function proofCopy(project: Project, proof: ProofRecord): ProofRecord {
  if (
    proof.projectId !== project.id ||
    (proof.tenantId && proof.tenantId !== project.tenantId)
  )
    throw new CustomizationError("CUSTOMIZATION_SCOPE_MISMATCH");
  if (
    !proof.id ||
    proof.id.length > 200 ||
    typeof proof.accepted !== "boolean" ||
    !Number.isFinite(Date.parse(proof.at))
  )
    throw new CustomizationError("CUSTOMIZATION_PROOF_INVALID");
  for (const key of ["name", "comments", "initials"] as const)
    if (
      typeof proof[key] !== "string" ||
      proof[key].length >
        (key === "initials" ? 10 : key === "comments" ? 10000 : 1000)
    )
      throw new CustomizationError("CUSTOMIZATION_PROOF_INVALID");
  if (proof.partId && !project.parts.some((part) => part.id === proof.partId))
    throw new CustomizationError("CUSTOMIZATION_PART_UNKNOWN");
  const values = parseContext({ dynamic: proof.values }).dynamic!;
  if (
    Object.keys(values).some(
      (id) => !project.fields.some((field) => field.id === id),
    )
  )
    throw new CustomizationError("CUSTOMIZATION_FIELD_UNKNOWN");
  return { ...structuredClone(proof), tenantId: project.tenantId, values };
}
function identities(
  project: Project,
  input: CustomizationIds,
  previous?: IdMap,
): IdMap {
  if (!integer(input.stateId) || !["portable", "database"].includes(input.mode))
    throw new CustomizationError("CUSTOMIZATION_IDENTITY_REQUIRED");
  if (input.mode === "database" && previous?.mode !== "database")
    previous = undefined;
  const used = new Set<number>();
  const map = (
    items: { id: string; legacy?: Record<string, unknown> }[],
    supplied: Record<string, number>,
    column: string,
    prefix: string,
    old: Record<string, number> = {},
  ) => {
    const result = new Map<string, number>();
    for (const key of Object.keys(supplied))
      if (!items.some((item) => item.id === key))
        throw new CustomizationError("CUSTOMIZATION_IDENTITY_UNKNOWN");
    for (const item of items) {
      const value =
        own(supplied, item.id) ??
        own(old, item.id) ??
        own(item.legacy, column) ??
        tokenId(item.id, prefix);
      if (Object.hasOwn(supplied, item.id) && !integer(value))
        throw new CustomizationError("CUSTOMIZATION_IDENTITY_INVALID");
      if (integer(value)) {
        if (used.has(value))
          throw new CustomizationError("CUSTOMIZATION_IDENTITY_DUPLICATE");
        used.add(value);
        result.set(item.id, value);
      }
    }
    let next = Math.max(0, ...used) + 1;
    if (input.mode === "portable")
      for (const item of items)
        if (!result.has(item.id)) {
          if (!integer(next))
            throw new CustomizationError("CUSTOMIZATION_IDENTITY_LIMIT");
          result.set(item.id, next);
          used.add(next++);
        }
    return Object.fromEntries(result);
  };
  const variableIds = map(
    project.fields,
    input.variableIds || {},
    "VARIABLE_ID",
    "variable",
    previous?.variableIds,
  );
  used.clear();
  const partIds = map(
    project.parts,
    input.partIds || {},
    "PART_ID",
    "part",
    previous?.partIds,
  );
  const mainPartId = input.mainPartId ?? previous?.mainPartId ?? 0;
  if (mainPartId !== 0 && !integer(mainPartId))
    throw new CustomizationError("CUSTOMIZATION_PART_ID_REQUIRED");
  if (mainPartId !== 0 && Object.values(partIds).includes(mainPartId))
    throw new CustomizationError("CUSTOMIZATION_IDENTITY_DUPLICATE");
  return {
    mode: input.mode,
    stateId: input.stateId,
    variableIds,
    partIds,
    mainPartId,
  };
}
function sidecars(envelope: LegacyEnvelope): Sidecar[] {
  const collection = own(envelope, "_proofCustomizations") as
    { version?: number; entries?: Sidecar[] } | undefined;
  const old = own(envelope, "_proofCustomization") as Sidecar | undefined;
  if (
    collection &&
    (collection.version !== 1 ||
      !Array.isArray(collection.entries) ||
      collection.entries.length > 10000)
  )
    throw new CustomizationError("CUSTOMIZATION_VERSION_UNSUPPORTED");
  const entries = collection?.entries || (old ? [old] : []),
    seen = new Set<number>();
  for (const entry of entries) {
    if (
      !entry ||
      entry.version !== 1 ||
      !integer(entry.ids?.stateId) ||
      seen.has(entry.ids.stateId) ||
      typeof entry.projectId !== "string" ||
      typeof entry.tenantId !== "string" ||
      !entry.proof ||
      typeof entry.digest !== "string"
    )
      throw new CustomizationError("CUSTOMIZATION_METADATA_INVALID");
    seen.add(entry.ids.stateId);
  }
  return entries;
}
const canonical = (value: unknown): string =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : item,
  );
function digest(
  state: LegacyRow,
  values: LegacyRow[],
  metadata: Omit<Sidecar, "digest">,
) {
  const ordered = [...values].sort((a, b) =>
    canonical(a) < canonical(b) ? -1 : canonical(a) > canonical(b) ? 1 : 0,
  );
  return bytesHash(
    new TextEncoder().encode(canonical({ state, values: ordered, metadata })),
  );
}
export function exportCustomization(
  project: Project,
  source: ProofRecord,
  input: CustomizationIds,
  original: LegacyEnvelope = { tables: {} },
): LegacyEnvelope {
  const proof = proofCopy(project, source),
    envelope = structuredClone(original);
  const metadataEntries = sidecars(envelope),
    oldMeta = metadataEntries.find(
      (entry) => entry.ids.stateId === input.stateId,
    );
  if (
    oldMeta &&
    (oldMeta.projectId !== project.id || oldMeta.tenantId !== project.tenantId)
  )
    throw new CustomizationError("CUSTOMIZATION_SCOPE_MISMATCH");
  const previous =
    oldMeta?.projectId === project.id && oldMeta.tenantId === project.tenantId
      ? oldMeta.ids
      : undefined;
  const ids = identities(project, input, previous);
  const partId = proof.partId ? ids.partIds[proof.partId] : ids.mainPartId;
  if (
    partId === undefined ||
    (input.mode === "database" && !proof.partId && partId === 0)
  )
    throw new CustomizationError("CUSTOMIZATION_PART_ID_REQUIRED");
  const sourceStates = rows(envelope, states),
    sourceValues = rows(envelope, variables);
  const sameState = (row: LegacyRow) =>
    numeric(row.CUSTOMIZATION_STATE_ID) === input.stateId;
  if (sourceStates.filter(sameState).length > 1)
    throw new CustomizationError("CUSTOMIZATION_STATE_AMBIGUOUS");
  const oldState = sourceStates.find(sameState) || {},
    oldRows = sourceValues.filter(sameState);
  const state = {
    ...oldState,
    CUSTOMIZATION_STATE_ID: input.stateId,
    PART_ID: partId,
    PROOF_COMMENTS: proof.comments,
    PROOF_IS_ACCEPTED: proof.accepted,
    INITIAL: proof.initials,
    IS_COMPLETE: input.complete ?? oldState.IS_COMPLETE ?? false,
    CREATE_DATE: oldState.CREATE_DATE ?? proof.at,
  };
  const flat = (value: Values[string]) =>
    value === null
      ? null
      : statementSelectionSchema.safeParse(value).success
        ? statementSelectionSchema.parse(value).text
        : typeof value === "object"
          ? JSON.stringify(value)
          : String(value);
  const known = (row: LegacyRow) =>
    project.fields.some(
      (field) =>
        ids.variableIds[field.id] === numeric(row.VARIABLE_ID) ||
        (field.key && field.key === row.VARIABLE_NAME),
    );
  const retained = sourceValues.filter((row) => !sameState(row) || !known(row));
  const usedRowIds = new Set(
    sourceValues.map((row) => numeric(row.ROW_ID)).filter(integer),
  );
  let nextRow = Math.max(0, ...usedRowIds) + 1;
  const current: LegacyRow[] = [];
  for (const [fieldId, value] of Object.entries(proof.values)) {
    const field = project.fields.find((field) => field.id === fieldId)!,
      variableId = ids.variableIds[fieldId];
    if (!integer(variableId))
      throw new CustomizationError("CUSTOMIZATION_VARIABLE_ID_REQUIRED");
    if (field.key.length > 100)
      throw new CustomizationError("CUSTOMIZATION_VARIABLE_NAME_LIMIT");
    const matches = oldRows.filter(
      (row) =>
        numeric(row.VARIABLE_ID) === variableId ||
        (!numeric(row.VARIABLE_ID) && row.VARIABLE_NAME === field.key),
    );
    if (matches.length > 1)
      throw new CustomizationError("CUSTOMIZATION_VALUE_AMBIGUOUS");
    const row = matches[0] || {};
    const rowId = integer(numeric(row.ROW_ID))
      ? numeric(row.ROW_ID)
      : input.mode === "portable"
        ? nextRow++
        : undefined;
    if (rowId !== undefined && !integer(rowId))
      throw new CustomizationError("CUSTOMIZATION_ROW_ID_LIMIT");
    current.push({
      ...row,
      ...(rowId !== undefined ? { ROW_ID: rowId } : {}),
      CUSTOMIZATION_STATE_ID: input.stateId,
      VARIABLE_ID: variableId,
      VARIABLE_NAME: field.key,
      VARIABLE_VALUE: flat(value),
    });
  }
  envelope.tables[states] = [
    ...sourceStates.filter((row) => !sameState(row)),
    state,
  ];
  envelope.tables[variables] = [...retained, ...current];
  const metadata: Omit<Sidecar, "digest"> = {
    version: 1,
    projectId: project.id,
    tenantId: project.tenantId,
    ids,
    proof,
  };
  const entry = {
    ...metadata,
    digest: digest(
      state,
      [...retained.filter(sameState), ...current],
      metadata,
    ),
  };
  envelope._proofCustomizations = {
    version: 1,
    entries: [
      ...metadataEntries.filter((entry) => entry.ids.stateId !== input.stateId),
      entry,
    ],
  };
  delete envelope._proofCustomization;
  return envelope;
}
export function importCustomization(
  project: Project,
  input: LegacyEnvelope,
  stateId?: number,
  options: {
    variableIds?: Record<string, number>;
    partIds?: Record<string, number>;
    mainPartId?: number;
    imageAssets?: Readonly<Record<string, string>>;
    mode?: "portable" | "database";
  } = {},
) {
  const envelope = structuredClone(input),
    sourceStates = rows(envelope, states);
  const selected =
    stateId === undefined
      ? sourceStates
      : sourceStates.filter(
          (row) => numeric(row.CUSTOMIZATION_STATE_ID) === stateId,
        );
  if (
    selected.length !== 1 ||
    !integer(numeric(selected[0].CUSTOMIZATION_STATE_ID))
  )
    throw new CustomizationError("CUSTOMIZATION_STATE_AMBIGUOUS");
  const state = selected[0],
    selectedId = numeric(state.CUSTOMIZATION_STATE_ID),
    data = rows(envelope, variables).filter(
      (row) => numeric(row.CUSTOMIZATION_STATE_ID) === selectedId,
    ),
    warnings: string[] = [];
  const meta = sidecars(envelope).find(
    (entry) => entry.ids.stateId === selectedId,
  );
  if (meta && options.mode && options.mode !== meta.ids.mode)
    throw new CustomizationError("CUSTOMIZATION_MODE_MISMATCH");
  if (
    meta &&
    (meta.projectId !== project.id ||
      meta.tenantId !== project.tenantId ||
      meta.ids?.stateId !== selectedId)
  )
    throw new CustomizationError("CUSTOMIZATION_SCOPE_MISMATCH");
  const ids = identities(
    project,
    {
      mode: meta?.ids?.mode || options.mode || "database",
      stateId: selectedId,
      variableIds: options.variableIds,
      partIds: options.partIds,
      mainPartId: options.mainPartId ?? meta?.ids?.mainPartId ?? 0,
    },
    meta?.ids,
  );
  const pairs: [string, unknown][] = [],
    seen = new Set<string>();
  for (const row of data) {
    const byId = project.fields.filter(
      (field) => ids.variableIds[field.id] === numeric(row.VARIABLE_ID),
    );
    const byName = project.fields.filter(
      (field) => field.key && field.key === row.VARIABLE_NAME,
    );
    if (
      byId.length > 1 ||
      byName.length > 1 ||
      (byId.length && byName.length && byId[0].id !== byName[0].id)
    )
      throw new CustomizationError("CUSTOMIZATION_FIELD_AMBIGUOUS");
    const field = byId[0] || byName[0];
    if (!field) {
      warnings.push(
        `Unmapped customization variable ${String(row.VARIABLE_ID)} retained in source`,
      );
      continue;
    }
    if (seen.has(field.id))
      throw new CustomizationError("CUSTOMIZATION_VALUE_AMBIGUOUS");
    seen.add(field.id);
    const value =
      row.VARIABLE_VALUE === null ? "" : String(row.VARIABLE_VALUE ?? "");
    const typed =
      row.VARIABLE_VALUE === null
        ? null
        : bindPrepopulation(
            [
              {
                id: "restore",
                label: "Restore",
                values: { [field.id]: value },
              },
            ],
            [field],
            undefined,
            options.imageAssets,
          ).presets[0].values[field.id];
    pairs.push([field.id, typed]);
  }
  const partNumber = numeric(state.PART_ID),
    partId = Object.entries(ids.partIds).find(
      ([, id]) => id === partNumber,
    )?.[0];
  if (
    (!integer(partNumber) && !(ids.mode === "portable" && partNumber === 0)) ||
    (!partId && partNumber !== ids.mainPartId)
  )
    throw new CustomizationError("CUSTOMIZATION_PART_UNKNOWN");
  let proof: ProofRecord = {
    id: `customization_${selectedId}`,
    tenantId: project.tenantId,
    projectId: project.id,
    partId,
    name: project.name,
    values: parseContext({ dynamic: Object.fromEntries(pairs) }).dynamic!,
    comments: String(state.PROOF_COMMENTS || ""),
    initials: String(state.INITIAL || ""),
    accepted: flag(state.PROOF_IS_ACCEPTED),
    at:
      typeof state.CREATE_DATE === "string" &&
      Number.isFinite(Date.parse(state.CREATE_DATE))
        ? state.CREATE_DATE
        : "1970-01-01T00:00:00.000Z",
  };
  if (!state.CREATE_DATE)
    warnings.push("Source save date is unknown; an epoch timestamp is used");
  if (meta) {
    const { digest: saved, ...metadata } = meta;
    if (meta.version !== 1)
      throw new CustomizationError("CUSTOMIZATION_VERSION_UNSUPPORTED");
    if (saved === digest(state, data, metadata))
      proof = proofCopy(project, meta.proof);
    else
      warnings.push(
        "Native customization sidecar is stale; restored table values",
      );
  }
  proof = proofCopy(project, proof);
  return { proof, complete: flag(state.IS_COMPLETE), envelope, ids, warnings };
}
